import {
  AddonLifecycleStatus,
  AddonOperation,
  AddonsAddonListItem,
} from '@api/types.gen';

/**
 * AddonLifecycleState collapses an add-on's lifecycleStatus (and status message)
 * into the named predicates the list card and the detail header both branch on.
 *
 * Both views derive the same set of booleans; computing them here in one place
 * keeps their action logic from drifting apart when only one view is touched.
 * Where the two views legitimately differ (`isMutating` vs `isInProgress`), both
 * concepts are exposed so each can keep its existing behaviour.
 */
export interface AddonLifecycleState {
  /** No lifecycle status at all: never installed, or fully uninstalled. */
  notInstalled: boolean;
  isInstalling: boolean;
  isUpgrading: boolean;
  isUninstalling: boolean;
  isInstalled: boolean;
  isFailed: boolean;
  isUnknown: boolean;
  /** The release is mid-change (upgrading or uninstalling), so editing it would
   *  race the running operation. Excludes a first install. */
  isMutating: boolean;
  /** Any lifecycle operation in flight, including a first install. */
  isInProgress: boolean;
  /** Installed, but a status message means the last upgrade failed and Helm
   *  rolled back to the previous version. A failed uninstall is not one. */
  upgradeFailed: boolean;
  /** Uninstall is offered for any settled-or-stuck state, but not for a failed
   *  first install (nothing to remove) or while already uninstalling. A failed
   *  uninstall is offered again, so the retry can finish removing the record
   *  and revoke its credential. */
  canUninstall: boolean;
}

export function getAddonLifecycleState(
  addon:
    | Pick<
        AddonsAddonListItem,
        'lifecycleStatus' | 'lifecycleStatusMessage' | 'failedOperation'
      >
    | undefined
): AddonLifecycleState {
  const status = addon?.lifecycleStatus;

  const isInstalling = status === AddonLifecycleStatus.ADDON_STATUS_INSTALLING;
  const isUpgrading = status === AddonLifecycleStatus.ADDON_STATUS_UPGRADING;
  const isUninstalling =
    status === AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING;
  const isInstalled = status === AddonLifecycleStatus.ADDON_STATUS_INSTALLED;
  const isFailed = status === AddonLifecycleStatus.ADDON_STATUS_FAILED;
  const isMutating = isUpgrading || isUninstalling;
  const uninstallFailed =
    isFailed && addon?.failedOperation === AddonOperation.OPERATION_UNINSTALL;

  return {
    notInstalled: !status,
    isInstalling,
    isUpgrading,
    isUninstalling,
    isInstalled,
    isFailed,
    isUnknown: status === AddonLifecycleStatus.ADDON_STATUS_UNKNOWN,
    isMutating,
    isInProgress: isMutating || isInstalling,
    upgradeFailed:
      isInstalled &&
      !!addon?.lifecycleStatusMessage &&
      addon.failedOperation !== AddonOperation.OPERATION_UNINSTALL,
    canUninstall: !!status && (!isFailed || uninstallFailed) && !isUninstalling,
  };
}
